<script setup lang="ts">
import { computed } from 'vue';

export type UiProgressBarSize = 'sm' | 'md' | 'lg';
export type UiProgressBarTone = 'green' | 'dark' | 'highlight' | 'warn' | 'danger';

export interface UiProgressBarProps {
    /** Current value of the progress bar. */
    value: number;
    /** Value that represents 100%. Defaults to 100. */
    max?: number;
    /** Thickness of the bar. */
    size?: UiProgressBarSize;
    /** Fill color of the bar. */
    tone?: UiProgressBarTone;
    /** Render the bar as an indeterminate/animated loading bar. */
    indeterminate?: boolean;
    /** Accessible label describing what the progress represents. */
    ariaLabel?: string;
    /** Extra classes for the outer track element. */
    trackClass?: string | string[] | Record<string, boolean>;
    /** Extra classes for the inner fill element. */
    barClass?: string | string[] | Record<string, boolean>;
}

const props = withDefaults(defineProps<UiProgressBarProps>(), {
    max: 100,
    size: 'md',
    tone: 'green',
    indeterminate: false,
    ariaLabel: undefined,
    trackClass: undefined,
    barClass: undefined,
});

const percent = computed(() => {
    const max = props.max <= 0 ? 1 : props.max;
    const ratio = (props.value / max) * 100;
    if (!Number.isFinite(ratio)) return 0;
    return Math.min(100, Math.max(0, ratio));
});

const heightClass = computed(() => {
    switch (props.size) {
        case 'sm':
            return 'h-1.5';
        case 'lg':
            return 'h-3';
        default:
            return 'h-2';
    }
});

const toneClass = computed(() => {
    switch (props.tone) {
        case 'dark':
            return 'bg-se-dark';
        case 'highlight':
            return 'bg-se-highlight';
        case 'warn':
            return 'bg-se-warn';
        case 'danger':
            return 'bg-se-danger';
        default:
            return 'bg-se-green';
    }
});
</script>

<template>
    <div
        class="w-full overflow-hidden rounded-full bg-se-gray-200"
        :class="[heightClass, props.trackClass]"
        role="progressbar"
        :aria-valuenow="props.indeterminate ? undefined : Math.round(percent)"
        :aria-valuemin="props.indeterminate ? undefined : 0"
        :aria-valuemax="props.indeterminate ? undefined : 100"
        :aria-label="props.ariaLabel"
    >
        <div
            v-if="props.indeterminate"
            class="h-full w-2/5 rounded-full ui-progress-bar-indeterminate"
            :class="[toneClass, props.barClass]"
        />
        <div
            v-else
            class="h-full rounded-full transition-[width] duration-700 ease-out"
            :class="[toneClass, props.barClass]"
            :style="{ width: `${percent}%` }"
        />
    </div>
</template>

<style scoped>
@keyframes ui-progress-bar-indeterminate {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(350%);
    }
}

.ui-progress-bar-indeterminate {
    animation: ui-progress-bar-indeterminate 1.2s ease-in-out infinite;
}
</style>
